//frontend/src/components/admin/CreateDeliveryStaff.js

// CreateDeliveryStaff.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    User,
    Mail,
    Lock,
    MapPin,
    Phone,
    Calendar,
    Save,
    Upload,
    Check,
    Eye,
    EyeOff,
    ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const InputField = React.memo(({ label, icon: Icon, rightIcon, onRightIconClick, ...props }) => (
    <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 mb-1">
            {label}
        </label>
        <div className="relative">
            <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                <Icon className="h-4 w-4 text-gray-400" />
            </div>
            <input
                className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-center text-sm"
                {...props}
            />
            {rightIcon && (
                <button
                    type="button"
                    className="absolute inset-y-0 right-2.5 flex items-center"
                    onClick={onRightIconClick}
                >
                    {rightIcon}
                </button>
            )}
        </div>
    </div>
));

const ServiceAreaCheckbox = React.memo(({ area, pincode, selected, onChange }) => (
    <div className="flex items-center space-x-1.5 p-1.5 hover:bg-gray-50 rounded-lg">
        <div 
            className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors
                ${selected ? 'bg-[#dc2626] border-[#dc2626]' : 'border-gray-300'}`}
            onClick={() => onChange(pincode)}
        >
            {selected && <Check className="h-3 w-3 text-white" />}
        </div>
        <label className="flex-1 cursor-pointer text-xs">
            {area} ({pincode})
        </label>
    </div>
));

const CreateDeliveryStaff = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        dateOfBirth: '',
        age: '',
        address: '',
        email: '',
        phone: '',
        loginId: '',
        password: '',
        assignedPincodes: [],
        profilePicture: null
    });

    const serviceAreas = {
        '562130': 'Gollarahatti',
        '562130': 'Kadabagere',
        '560112': 'Chikka Gollarahatti',
        '560091': 'Machohalli',
        '560091': 'Anjana Nagar',
        '560091': 'Byadarahalli',
        '562130': 'Tavarekere',
        '560091': 'Herohalli',
        '560091': 'Viswaneedam',
        '560091': 'Sunkadakatte',
        '560091': 'Muddayanapalya',
        '560091': 'Annapurneshwari Nagar',
        '560091': 'Kottigepalya',
        '560091': 'Sumanahalli'
    };

    useEffect(() => {
        if (formData.dateOfBirth) {
            const birthDate = new Date(formData.dateOfBirth);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            
            setFormData(prev => ({
                ...prev,
                age: age.toString()
            }));
        }
    }, [formData.dateOfBirth]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        
        if (name === 'phone') {
            const phoneNumber = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({
                ...prev,
                phone: phoneNumber
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleAreaToggle = useCallback((pincode) => {
        setFormData(prev => ({
            ...prev,
            assignedPincodes: prev.assignedPincodes.includes(pincode)
                ? prev.assignedPincodes.filter(p => p !== pincode)
                : [...prev.assignedPincodes, pincode]
        }));
    }, []);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }
            setFormData(prev => ({
                ...prev,
                profilePicture: file
            }));
        }
    }, []);

    const togglePasswordVisibility = useCallback(() => {
        setShowPassword(prev => !prev);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
    
        try {
            // Validate fields before submission
            if (!formData.name.trim()) {
                toast.error('Please enter delivery staff name');
                setLoading(false);
                return;
            }
            if (!formData.dateOfBirth) {
                toast.error('Please enter date of birth');
                setLoading(false);
                return;
            }
            if (!formData.phone || formData.phone.length !== 10) {
                toast.error('Please enter valid 10-digit phone number');
                setLoading(false);
                return;
            }
            if (!formData.address.trim()) {
                toast.error('Please enter address');
                setLoading(false);
                return;
            }
            if (formData.assignedPincodes.length === 0) {
                toast.error('Please select at least one service area');
                setLoading(false);
                return;
            }
            if (!formData.loginId.trim()) {
                toast.error('Please enter login ID');
                setLoading(false);
                return;
            }
            if (!formData.password.trim()) {
                toast.error('Please enter password');
                setLoading(false);
                return;
            }
    
            // Create FormData object
            const formPayload = new FormData();
    
            // Append all form fields, ensuring proper trimming and type conversion
            Object.entries(formData).forEach(([key, value]) => {
                if (key === 'assignedPincodes') {
                    // Handle array of pincodes
                    value.forEach(pincode => {
                        formPayload.append('assignedPincodes[]', pincode);
                    });
                } else if (key === 'profilePicture' && value) {
                    // Handle file upload
                    formPayload.append('profilePicture', value);
                } else if (value !== null && value !== undefined) {
                    // Handle all other fields
                    formPayload.append(key, String(value).trim());
                }
            });
    
            const response = await axios.post('/api/v1/admin/delivery-staff/new', formPayload, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true
            });
    
            if (response.data.success) {
                toast.success('Delivery staff created successfully');
                navigate('/delivery-staff-list');
            }
        } catch (error) {
            console.error('Error details:', error.response?.data);
            toast.error(error.response?.data?.message || 'Error creating delivery staff');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Delivery Staff List Button - Right aligned */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => navigate('/delivery-staff-list')}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Delivery Staff List</span>
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
                <h2 className="text-xl font-bold text-[#dc2626] mb-4 font-['Crimson_Text'] text-center">
                    Create New Delivery Staff
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <InputField
                        label="Full Name"
                        icon={User}
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter full name"
                        required
                    />

                    <InputField
                        label="Date of Birth"
                        icon={Calendar}
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split('T')[0]}
                        required
                    />

                    <div className="flex items-center space-x-2 mb-3">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                                <User className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={`${formData.age} years`}
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg bg-gray-50 text-center text-sm"
                                readOnly
                            />
                        </div>
                    </div>

                    <InputField
                        label="Phone Number"
                        icon={Phone}
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter 10-digit phone number"
                        required
                    />

                    <InputField
                        label="Email"
                        icon={Mail}
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter email address"
                    />

                    <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Address
                        </label>
                        <div className="relative">
                            <div className="absolute top-2 left-2.5">
                                <MapPin className="h-4 w-4 text-gray-400" />
                            </div>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                rows="3"
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-center text-sm"
                                placeholder="Enter full address"
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Assign Service (Delivery) Areas
                        </label>
                        <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-1.5">
                            {Object.entries(serviceAreas).map(([pincode, area]) => (
                                <ServiceAreaCheckbox
                                    key={pincode}
                                    area={area}
                                    pincode={pincode}
                                    selected={formData.assignedPincodes.includes(pincode)}
                                    onChange={handleAreaToggle}
                                />
                            ))}
                        </div>
                    </div>

                    <InputField
                        label="Login ID"
                        icon={User}
                        type="text"
                        name="loginId"
                        value={formData.loginId}
                        onChange={handleInputChange}
                        placeholder="Enter login ID"
                        required
                    />

                    <InputField
                        label="Password"
                        icon={Lock}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter password"
                        required
                        rightIcon={
                            showPassword ? (
                                <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                            ) : (
                                <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                            )
                        }
                        onRightIconClick={togglePasswordVisibility}
                    />

                    <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Profile Picture (Optional)
                        </label>
                        <div className="mt-1 flex items-center justify-center">
                            <div className="flex flex-col items-center">
                                {formData.profilePicture ? (
                                    <div className="mb-2">
                                        <img
                                            src={URL.createObjectURL(formData.profilePicture)}
                                            alt="Profile preview"
                                            className="h-20 w-20 rounded-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="mb-2 h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center">
                                        <User className="h-10 w-10 text-gray-400" />
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="profile-picture"
                                />
                                <label
                                    htmlFor="profile-picture"
                                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] cursor-pointer"
                                >
                                    <Upload className="h-4 w-4 mr-1.5" />
                                    {formData.profilePicture ? 'Change Photo' : 'Upload Photo'}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] transition-colors disabled:opacity-50"
                        >
                            <Save className="w-4 h-4 mr-1.5" />
                            {loading ? 'Creating...' : 'Create Delivery Staff'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Mobile Optimization Styles */}
            <style jsx>{`
                @media (max-width: 640px) {
                    .text-sm {
                        font-size: 0.75rem;
                        line-height: 1rem;
                    }
                    
                    .p-4 {
                        padding: 0.75rem;
                    }
                    
                    .space-y-4 > :not([hidden]) ~ :not([hidden]) {
                        --tw-space-y-reverse: 0;
                        margin-top: calc(0.75rem * calc(1 - var(--tw-space-y-reverse)));
                        margin-bottom: calc(0.75rem * var(--tw-space-y-reverse));
                    }
                    
                    .py-1\\.5 {
                        padding-top: 0.25rem;
                        padding-bottom: 0.25rem;
                    }
                    
                    .mb-4 {
                        margin-bottom: 0.75rem;
                    }
                    
                    .mb-3 {
                        margin-bottom: 0.5rem;
                    }
                    
                    .mb-1 {
                        margin-bottom: 0.25rem;
                    }
                    
                    .h-20 {
                        height: 5rem;
                    }
                    
                    .w-20 {
                        width: 5rem;
                    }
                    
                    .h-10 {
                        height: 2.5rem;
                    }
                    
                    .w-10 {
                        width: 2.5rem;
                    }
                    
                    .px-3 {
                        padding-left: 0.5rem;
                        padding-right: 0.5rem;
                    }
                    
                    .py-1\\.5 {
                        padding-top: 0.25rem;
                        padding-bottom: 0.25rem;
                    }
                    
                    .text-xl {
                        font-size: 1.125rem;
                        line-height: 1.75rem;
                    }
                    
                    .max-h-40 {
                        max-height: 8rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default CreateDeliveryStaff;